import React, { useState, useEffect } from "react";
import { Table, Card, Tag, Input, Button } from "antd";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";

const Carts = () => {
  const [groupedData, setGroupedData] = useState([]); 
  const [loading, setLoading] = useState(false);
  
  // 🟢 NEW: State to track which rows are open
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50, 
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchAdminCartData({ page: pagination.current - 1, size: pagination.pageSize });
  }, [pagination.current, pagination.pageSize]);

  const fetchAdminCartData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/usercart/admin/all`, { params });
      const { content, totalElements } = response.data;
      
      const grouped = groupCartData(content);
      
      setGroupedData(grouped);
      setPagination((prev) => ({
        ...prev,
        total: totalElements,
        current: (params.page || 0) + 1,
      }));
    } catch (error) {
      console.error("Error fetching admin cart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupCartData = (flatList) => {
    const groups = {};

    flatList.forEach((item) => {
      const uniqueKey = item.userEmail || item.userMobile; 

      if (!groups[uniqueKey]) {
        groups[uniqueKey] = {
          key: uniqueKey,
          userName: item.userName,
          userMobile: item.userMobile,
          userEmail: item.userEmail,
          totalCartValue: 0,
          totalItems: 0,
          products: [] 
        };
      }

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;

      groups[uniqueKey].products.push({
          ...item,
          quantity: qty,
          price: price
      });
      
      groups[uniqueKey].totalItems += qty;
      groups[uniqueKey].totalCartValue += (qty * price);
    });

    return Object.values(groups);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  // 🟢 NEW: Handler to toggle row expansion
  const toggleRow = (key) => {
    const isExpanded = expandedRowKeys.includes(key);
    if (isExpanded) {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  };

  const parentColumns = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      render: (text) => <span className="font-bold text-lg">{text}</span>,
    },
    {
      title: "Contact Info",
      key: "contact",
      render: (_, record) => (
        <div>
          <div className="text-gray-600">📧 {record.userEmail}</div>
          <div className="text-gray-500">📞 {record.userMobile}</div>
        </div>
      ),
    },
    {
      title: "Cart Summary",
      key: "summary",
      align: 'center',
      render: (_, record) => (
        <Tag color="green" style={{ fontSize: '14px', padding: '4px 10px' }}>
           {record.totalItems} Items
        </Tag>
      ),
    },
    {
      title: "Total Cart Value",
      key: "totalValue",
      align: 'right',
      render: (_, record) => (
        <span className="font-bold text-green-700 text-lg">
          ₹ {record.totalCartValue.toFixed(2)}
        </span>
      ),
    },
    // 🟢 NEW: Explicit View Column
    {
      title: "Action",
      key: "action",
      align: 'center',
      render: (_, record) => {
        const isOpen = expandedRowKeys.includes(record.key);
        return (
          <Button 
            type={isOpen ? "default" : "primary"} 
            onClick={() => toggleRow(record.key)}
            icon={isOpen ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            style={{ 
                // Blue (#1890ff) for 'View', Dark Grey (#555) for 'Hide'
                backgroundColor: isOpen ? "#555" : "#1890ff", 
                borderColor: isOpen ? "#555" : "#1890ff",
                color: "#fff",
                fontWeight: "bold"
            }}
          >
            {isOpen ? "Hide Products" : "View Products"}
          </Button>
        );
      },
    },
  ];

  const expandedRowRender = (userRecord) => {
    const childColumns = [
      {
        title: "Product Name",
        dataIndex: "productName",
        key: "productName",
        render: (text, r) => (
            <span>
                <span className="font-medium">{text}</span>
                <div className="text-xs text-gray-400">ID: {r.productId}</div>
            </span>
        )
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (val) => `₹${val.toFixed(2)}`,
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        render: (qty) => <Tag color="blue">x {qty}</Tag>,
      },
      {
        title: "Subtotal",
        key: "subtotal",
        align: 'right',
        render: (_, r) => <strong>₹ {(r.price * r.quantity).toFixed(2)}</strong>,
      },
    ];

    return (
      <Table 
        columns={childColumns} 
        dataSource={userRecord.products} 
        pagination={false} 
        rowKey="cartId" 
        size="small"
        bordered={false}
      />
    );
  };

  const filteredData = groupedData.filter((user) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    
    const userMatch = (
        (user.userName && user.userName.toLowerCase().includes(searchLower)) ||
        (user.userMobile && user.userMobile.includes(searchText)) ||
        (user.userEmail && user.userEmail.toLowerCase().includes(searchLower))
    );

    const productMatch = user.products.some(p => 
        p.productName.toLowerCase().includes(searchLower)
    );

    return userMatch || productMatch;
  });

  return (
    <div className="container mx-auto p-6">
      <Card title="User Carts Overview" className="shadow-md rounded-lg">
        
        <div className="mb-4 flex justify-between items-center">
          <Input.Search
            placeholder="Search by User Name"
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 400 }}
          />
          <Button type="primary" onClick={() => fetchAdminCartData()}>
            Refresh Data
          </Button>
        </div>

        <Table
          className="components-table-demo-nested"
          columns={parentColumns}
          dataSource={filteredData}
          rowKey="key" 
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items fetched`, 
          }}
          onChange={handleTableChange}
          
          // 🟢 UPDATED: Expandable Configuration
          expandable={{ 
            expandedRowRender,
            expandedRowKeys: expandedRowKeys, // Controlled state
            onExpand: (expanded, record) => toggleRow(record.key), // Handle + click if visible
            expandIconColumnIndex: -1 // Hides the default + column
          }}
        />
      </Card>
    </div>
  );
};

export default Carts;